import React, { useEffect, useState } from 'react';
import { adminApi, fmtDate, fmtCurrency } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StatusPill } from '@/components/fintech/StatusPill';
import { CheckCircle2, XCircle, Eye, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCompaniesPage() {
  const [items, setItems] = useState([]);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [status, setStatus] = useState('PENDING');
  const [selected, setSelected] = useState(null);
  const [reviewing, setReviewing] = useState(null); // {company, decision}
  const [reason, setReason] = useState('');

  const load = () => {
    adminApi.get('/admin/b2b/companies', { params: status !== 'all' ? { status } : {} }).then(r => setItems(r.data.items || []));
    adminApi.get('/admin/b2b/company-deposits', { params: { status: 'PENDING' } }).then(r => setPendingDeposits(r.data.items || []));
  };
  useEffect(() => { load(); }, [status]);

  const submitReview = async () => {
    if (!reviewing) return;
    try {
      await adminApi.post(`/admin/b2b/companies/${reviewing.company.company_id}/review`, { decision: reviewing.decision, reason });
      toast.success(`Company ${reviewing.decision}d`);
      setReviewing(null); setReason(''); load();
    } catch (err) { toast.error('Failed'); }
  };

  const approveDeposit = async (id) => { await adminApi.post(`/admin/b2b/company-deposits/${id}/approve`); toast.success('Approved'); load(); };
  const rejectDeposit = async (id) => { await adminApi.post(`/admin/b2b/company-deposits/${id}/reject`, { reason: 'Rejected by admin' }); toast.success('Rejected'); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">Companies</h1>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-48"><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="PENDING">Pending</SelectItem>
          <SelectItem value="APPROVED">Approved</SelectItem>
          <SelectItem value="REJECTED">Rejected</SelectItem>
        </SelectContent></Select>
      </div>

      {pendingDeposits.length > 0 && (
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="font-serif text-lg mb-3">Company deposit requests</div>
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--secondary))]"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Company</th><th className="p-2 text-left">Currency</th><th className="p-2 text-right">Amount</th><th className="p-2 text-right">Actions</th></tr></thead>
              <tbody>
                {pendingDeposits.map(d => (
                  <tr key={d.id} className="border-t">
                    <td className="p-2 text-xs">{fmtDate(d.created_at)}</td>
                    <td className="p-2">{d.company?.legal_name}</td>
                    <td className="p-2">{d.currency}</td>
                    <td className="p-2 text-right tabular-nums">{fmtCurrency(d.amount, d.currency)}</td>
                    <td className="p-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" onClick={() => approveDeposit(d.id)} className="bg-[hsl(152_45%_34%)]"><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="destructive" onClick={() => rejectDeposit(d.id)}><XCircle className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map(c => (
          <Card key={c.company_id} className="bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-[hsl(var(--brand-navy))]" /><div className="font-semibold">{c.legal_name}</div></div>
                <StatusPill status={c.verification_status} />
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Reg N° {c.registration_number} · {c.country}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Owner: {c.user?.email}</div>
              <div className="mt-3 text-xs">Submitted: {fmtDate(c.created_at)}</div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <Button size="sm" variant="ghost" onClick={() => setSelected(c)} data-testid={`company-details-${c.company_id}`}><Eye className="h-4 w-4 mr-1" />Details</Button>
                {c.verification_status === 'PENDING' && <>
                  <Button size="sm" onClick={() => setReviewing({ company: c, decision: 'approve' })} className="bg-[hsl(152_45%_34%)]" data-testid={`company-approve-${c.company_id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>
                  <Button size="sm" variant="destructive" onClick={() => setReviewing({ company: c, decision: 'reject' })}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                </>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {items.length === 0 && <Card className="bg-white"><CardContent className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No companies</CardContent></Card>}

      <Dialog open={!!selected} onOpenChange={o => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{selected?.legal_name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-2 text-sm">
              <div><span className="text-[hsl(var(--muted-foreground))]">Registration N°:</span> {selected.registration_number}</div>
              <div><span className="text-[hsl(var(--muted-foreground))]">Country:</span> {selected.country}</div>
              <div><span className="text-[hsl(var(--muted-foreground))]">Address:</span> {selected.address}</div>
              {selected.tax_number && <div><span className="text-[hsl(var(--muted-foreground))]">Tax:</span> {selected.tax_number}</div>}
              {selected.bank_name && <div><span className="text-[hsl(var(--muted-foreground))]">Bank:</span> {selected.bank_name} · {selected.bank_account} · {selected.swift_bic}</div>}
              {selected.website && <div><span className="text-[hsl(var(--muted-foreground))]">Website:</span> {selected.website}</div>}
              {selected.contact_person && <div><span className="text-[hsl(var(--muted-foreground))]">Contact:</span> {selected.contact_person} · {selected.contact_phone}</div>}
              <div><span className="text-[hsl(var(--muted-foreground))]">Owner user:</span> {selected.user?.email}</div>
              <div className="mt-3 font-semibold">Balances</div>
              {Object.entries(selected.balances || {}).map(([c, v]) => <div key={c} className="flex justify-between"><span>{c}</span><span className="tabular-nums">{fmtCurrency(v, c)}</span></div>)}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewing} onOpenChange={o => !o && setReviewing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{reviewing?.decision === 'approve' ? 'Approve' : 'Reject'} company</DialogTitle></DialogHeader>
          <div className="text-sm">{reviewing?.company?.legal_name}</div>
          <Textarea placeholder="Reason / note" value={reason} onChange={e => setReason(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewing(null)}>Cancel</Button>
            <Button onClick={submitReview} className={reviewing?.decision === 'approve' ? 'bg-[hsl(152_45%_34%)]' : 'bg-[hsl(var(--destructive))]'}>{reviewing?.decision === 'approve' ? 'Approve' : 'Reject'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
